import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  Grid,
  LinearProgress
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PaymentIcon from '@mui/icons-material/Payment';
import HistoryIcon from '@mui/icons-material/History';
import CancelIcon from '@mui/icons-material/Cancel';

export interface Subscription {
  id: string;
  planId: string;
  planName: string;
  status: 'active' | 'canceled' | 'past_due' | 'trialing' | 'incomplete';
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  cancelAtPeriodEnd: boolean;
  trialEnd?: Date;
}

interface SubscriptionStatusProps {
  subscription: Subscription | null;
  onManageSubscription: () => void;
  onCancelSubscription: () => void;
  onViewBillingHistory: () => void;
}

const SubscriptionStatus: React.FC<SubscriptionStatusProps> = ({
  subscription,
  onManageSubscription,
  onCancelSubscription,
  onViewBillingHistory
}) => {
  if (!subscription) {
    return (
      <Box sx={{ width: '100%' }}>
        <Typography variant="h5" component="h2" gutterBottom>
          No Active Subscription
        </Typography>
        <Typography variant="body1" paragraph>
          You don't have an active subscription. Choose a plan to get started.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button 
            variant="contained" 
            sx={{
              backgroundColor: '#E95380',
              '&:hover': { backgroundColor: '#d03e6a' }
            }}
            onClick={onManageSubscription}
          >
            View Plans
          </Button>
        </Box>
      </Box>
    );
  }

  // Calculate days remaining in subscription or trial
  const today = new Date();
  const endDate = subscription.trialEnd || subscription.currentPeriodEnd;
  const totalDays = Math.ceil((endDate.getTime() - subscription.currentPeriodStart.getTime()) / (1000 * 60 * 60 * 24));
  const daysRemaining = Math.max(0, Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
  const progress = Math.max(0, Math.min(100, ((totalDays - daysRemaining) / totalDays) * 100));

  // Format dates
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" component="h2" gutterBottom>
        Subscription Status
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {subscription.planName}
            </Typography>
            <Chip 
              label={subscription.status === 'active' ? 'Active' : 
                    subscription.status === 'trialing' ? 'Trial' : 
                    subscription.status === 'canceled' ? 'Canceled' :
                    subscription.status === 'past_due' ? 'Past Due' : 'Incomplete'}
              color={subscription.status === 'active' || subscription.status === 'trialing' ? 'success' : 
                    subscription.status === 'canceled' ? 'default' : 'error'}
              variant="outlined"
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CalendarTodayIcon sx={{ mr: 1, fontSize: '1rem' }} color="action" />
                <Typography variant="body2" color="text.secondary">
                  Current Period
                </Typography>
              </Box>
              <Typography variant="body1">
                {formatDate(subscription.currentPeriodStart)} - {formatDate(subscription.currentPeriodEnd)}
              </Typography>
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PaymentIcon sx={{ mr: 1, fontSize: '1rem' }} color="action" />
                <Typography variant="body2" color="text.secondary">
                  {subscription.status === 'trialing' ? 'Trial Ends' : 'Next Billing Date'}
                </Typography>
              </Box>
              <Typography variant="body1">
                {formatDate(subscription.trialEnd || subscription.currentPeriodEnd)}
              </Typography>
            </Box>
          </Box>

          {subscription.status === 'active' || subscription.status === 'trialing' ? (
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">
                  {subscription.status === 'trialing' ? 'Trial Progress' : 'Billing Period Progress'}
                </Typography>
                <Typography variant="body2">
                  {daysRemaining} days remaining
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={progress} 
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          ) : null}

          {subscription.cancelAtPeriodEnd && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
              <Typography variant="body2">
                Your subscription will be canceled at the end of the current billing period.
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Button 
              variant="outlined" 
              startIcon={<HistoryIcon />}
              onClick={onViewBillingHistory}
            >
              Billing History
            </Button>
            
            {!subscription.cancelAtPeriodEnd && subscription.status !== 'canceled' && (
              <Button 
                variant="outlined" 
                color="error"
                startIcon={<CancelIcon />}
                onClick={onCancelSubscription}
              >
                Cancel Subscription
              </Button>
            )}
            
            <Button 
              variant="contained"
              onClick={onManageSubscription}
            >
              Manage Subscription
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SubscriptionStatus;
